import {
  Box,
  Stack,
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
} from '@mui/material';
import RegulationFeed from '../components/RegulationFeed/RegulationFeed';
import { useRegulationFeed } from '../hooks/useRegulationFeed';
import { useTranslation } from 'react-i18next';
import { useContext, useMemo } from 'react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import PageHeader from '../components/Page/PageHeader';
import RegulationFeedSkeleton from '../components/RegulationFeed/RegulationFeedSkeleton';
import ErrorView from '../components/ErrorViews/ErrorView';
import { AuthContext } from '../components/Auth/AuthProvider';
import OpenGraphGeneral from '../components/MetaData/OpenGraphGeneral';
import { apiStates } from '../types/types';
import CategoryFiltersList from '../components/Category/CategoryFilters/List/CategoryFiltersList';
import CategoryFiltersDrawer from '../components/Category/CategoryFilters/Drawer/CategoryFiltersDrawer';
import useContentSearchParams from '../hooks/useContentSearchParams';
import { getRegulationFeedCategories } from '../utilities/Categories';
import { visuallyHidden } from '@mui/utils';

export default function RegulationFeedPage() {
  const { t } = useTranslation();
  const { actions, loading } = useContext(AuthContext);
  const { category: activeCategory, updateCategory } = useContentSearchParams();

  const pageTitle = useMemo(() => {
    let title = t('regulationFeed.titleLong');

    if (activeCategory) {
      title += `: ${activeCategory}`;
    }

    return title;
  }, [t, activeCategory]);

  useDocumentTitle(pageTitle);

  const theme = useTheme();
  const showMobileMenu = !useMediaQuery(theme.breakpoints.up('md'));

  const {
    state: apiState,
    regulationFeedList,
    apiError,
  } = useRegulationFeed(actions, loading);

  const categories = useMemo(() => {
    return getRegulationFeedCategories().sort((a, b) => {
      return a.localeCompare(b);
    });
  }, []);

  return (
    <Box>
      <PageHeader
        title={pageTitle}
        titleItem={
          showMobileMenu ? (
            <CategoryFiltersDrawer
              activeCategory={activeCategory}
              categories={categories}
              onSelectCategory={updateCategory}
            />
          ) : undefined
        }
      />
      <Grid container component='section' spacing={3}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          {showMobileMenu == false && (
            <CategoryFiltersList
              activeCategory={activeCategory}
              categories={categories}
              onSelectCategory={updateCategory}
            />
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        >
          {apiState === apiStates.HAS_ERROR && (
            <ErrorView apiError={apiError} />
          )}
          {apiState !== apiStates.HAS_ERROR && (
            <>
              <Stack direction='row' spacing={1}>
                <Typography
                  variant='subtitle2'
                  sx={{ paddingBottom: 1, paddingLeft: 2, ...visuallyHidden }}
                >
                  {activeCategory ?? t('regulationFeed.allCategories')}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ paddingBottom: 1, color: 'text.secondary' }}
                >
                  {t('regulationFeed.lastXDays', { days: 7 })}
                </Typography>
              </Stack>
              <Stack>
                {apiState === apiStates.FETCHING && <RegulationFeedSkeleton />}
                {apiState === apiStates.READY && (
                  <RegulationFeed
                    feed={regulationFeedList}
                    category={activeCategory}
                  />
                )}
              </Stack>
            </>
          )}
        </Grid>
      </Grid>
      <OpenGraphGeneral />
    </Box>
  );
}
